import { useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'eco-input mt-1.5';
const EMPTY = { name: '', email: '', whatsapp: '', travel_date: '', end_date: '', adults: '2', children: '0', accommodation: '', message: '', consent_terms: false };

const Field = ({ label, required = false, children, wide = false }) => (
  <label className={wide ? 'sm:col-span-2' : ''}>
    <span className="eco-field-label">{label}{required ? ' *' : ''}</span>
    {children}
  </label>
);

export default function RequestProposalPage() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const { packageBySlug, packagesLoaded } = useCatalog();
  const packageSlug = params.get('package') || '';
  const selectedPackage = packageBySlug(packageSlug);
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');

  useSEO(t('requestProposal.seo'), t('price.requestNote'));
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const totalTravellers = Math.max(0, Number(form.adults) || 0) + Math.max(0, Number(form.children) || 0);
  const advance = () => {
    setError('');
    if (step === 1 && (!form.travel_date || Number(form.adults) < 1 || Number(form.children) < 0 || (form.end_date && form.end_date < form.travel_date))) {
      setError(t('requestProposal.validation')); return;
    }
    if (step === 2 && !form.accommodation) { setError(t('requestProposal.validation')); return; }
    setStep((current) => Math.min(3, current + 1));
  };

  if (packagesLoaded && !selectedPackage) return <Navigate to="/full-packages" replace />;
  if (!selectedPackage) return <div className="min-h-[60vh] bg-eco-cream pt-32" aria-busy="true" />;

  const packagePath = `/full-packages/${selectedPackage.slug}`;
  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    try {
      const response = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'proposal',
          name: form.name.trim(),
          email: form.email.trim(),
          whatsapp: form.whatsapp.trim(),
          travel_date: form.travel_date,
          departure_date: form.end_date,
          dates: form.end_date ? `${form.travel_date} — ${form.end_date}` : form.travel_date,
          travelers: String(totalTravellers),
          adults: form.adults,
          children: form.children,
          accommodation: form.accommodation,
          message: form.message.trim(),
          special_requirements: form.message.trim(),
          destination: selectedPackage.destination,
          product: selectedPackage.name,
          package_id: selectedPackage.id || '',
          package_slug: selectedPackage.slug,
          package_name: selectedPackage.name,
          preferred_language: lang.toUpperCase(),
          consent_terms: form.consent_terms,
        }),
      });
      if (!response.ok) throw new Error('request_failed');
      const data = await response.json();
      setReference(data.reference || '');
    } catch {
      setError(t('requestProposal.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="request-proposal-page" className="bg-eco-cream pt-28 pb-16 md:pt-32 md:pb-24">
      <div className="mx-auto max-w-2xl px-5 md:px-8">
        <Link to={packagePath} className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-eco-green hover:text-eco-deep">
          <ArrowLeft size={15} /> {t('requestProposal.back')}
        </Link>
        {reference ? (
          <section data-testid="request-proposal-success" className="mt-5 rounded-2xl border border-eco-green/20 bg-white p-7 shadow-sm md:p-10">
            <CheckCircle2 size={38} className="text-eco-green" />
            <h1 className="mt-5 text-3xl font-semibold tracking-[-0.025em] text-eco-ink">{t('requestProposal.successTitle')}</h1>
            <p className="mt-3 leading-relaxed text-eco-ink/65">{t('requestProposal.successText')}</p>
            <p data-testid="request-proposal-reference" className="eco-number mt-4 text-sm font-semibold text-eco-gold">{reference}</p>
            <Link to={packagePath} data-testid="request-proposal-return" className="eco-btn-primary mt-7">
              {t('requestProposal.back')} <ArrowRight size={15} />
            </Link>
          </section>
        ) : (
          <section className="mt-5 rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm md:p-9">
            <h1 className="text-3xl font-semibold tracking-[-0.025em] text-eco-ink">{t('requestProposal.title')}</h1>
            <div className="mt-5 rounded-xl bg-eco-green/5 p-4">
              <p className="eco-field-label">{t('requestProposal.selected')}</p>
              <p data-testid="request-proposal-package" className="mt-1 font-semibold text-eco-ink">{selectedPackage.name}</p>
              <p className="mt-3 text-sm font-semibold uppercase tracking-[0.08em] text-eco-green">{t('price.onrequest')}</p>
              <p className="mt-1 text-sm leading-relaxed text-eco-ink/60">{t('price.requestNote')}</p>
            </div>
            <ol className="mt-6 grid grid-cols-3 gap-2" aria-label={t('requestProposal.title')}>
              {[t('requestProposal.stepTrip'), t('requestProposal.stepStay'), t('requestProposal.stepContact')].map((label, index) => <li key={label} aria-current={step === index + 1 ? 'step' : undefined} className={`rounded-full px-2 py-2 text-center text-xs font-semibold ${step === index + 1 ? 'bg-eco-green text-white' : step > index + 1 ? 'bg-eco-gold/20 text-eco-green' : 'bg-eco-cream text-eco-ink/50'}`}>{index + 1}. {label}</li>)}
            </ol>
            <form data-testid="request-proposal-form" onSubmit={submit} className="mt-7 grid gap-5 sm:grid-cols-2">
              {step === 1 && <><Field label={t('requestProposal.date')} required><input required type="date" min={new Date().toISOString().split('T')[0]} value={form.travel_date} onChange={(e) => set('travel_date', e.target.value)} className={inputCls} data-testid="request-proposal-date" /></Field><Field label={t('requestProposal.endDate')}><input type="date" min={form.travel_date || new Date().toISOString().split('T')[0]} value={form.end_date} onChange={(e) => set('end_date', e.target.value)} className={inputCls} data-testid="request-proposal-end-date" /></Field><Field label={t('form.adults')} required><input required type="number" min="1" inputMode="numeric" value={form.adults} onChange={(e) => set('adults', e.target.value)} className={inputCls} data-testid="request-proposal-adults" /></Field><Field label={t('form.children')}><input type="number" min="0" inputMode="numeric" value={form.children} onChange={(e) => set('children', e.target.value)} className={inputCls} data-testid="request-proposal-children" /></Field></>}
              {step === 2 && <fieldset className="sm:col-span-2"><legend className="eco-field-label">{t('requestProposal.accommodation')} *</legend><div className="mt-3 grid gap-3 sm:grid-cols-3">{[['economy','requestProposal.economy'],['standard','requestProposal.standard'],['premium','requestProposal.premium']].map(([value,key]) => <label key={value} className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-4 ${form.accommodation === value ? 'border-eco-green bg-eco-green/5' : 'border-eco-ink/15 bg-white'}`}><input required type="radio" name="accommodation" value={value} checked={form.accommodation === value} onChange={(e) => set('accommodation', e.target.value)} className="accent-eco-green" /> {t(key)}</label>)}</div></fieldset>}
              {step === 3 && <><Field label={t('form.name')} required wide><input required autoComplete="name" value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} data-testid="request-proposal-name" /></Field><Field label={t('form.email')} required><input required type="email" autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="request-proposal-email" /></Field><Field label={t('requestProposal.phone')}><input type="tel" autoComplete="tel" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className={inputCls} data-testid="request-proposal-phone" /></Field><Field label={t('requestProposal.message')} wide><textarea rows={4} value={form.message} onChange={(e) => set('message', e.target.value)} className={inputCls} data-testid="request-proposal-message" /></Field><label className="sm:col-span-2 flex min-h-11 items-start gap-3 text-sm leading-relaxed text-eco-ink/65"><input required type="checkbox" checked={form.consent_terms} onChange={(e) => set('consent_terms', e.target.checked)} className="mt-1 h-4 w-4 accent-eco-green" data-testid="request-proposal-consent" /><span>{t('requestProposal.consent')}</span></label></>}
              {error && <p role="alert" data-testid="request-proposal-error" className="sm:col-span-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>}
              <div className="sm:col-span-2 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">{step > 1 && <button type="button" onClick={() => { setError(''); setStep((current) => current - 1); }} className="eco-btn-secondary justify-center">{t('requestProposal.previous')}</button>}{step < 3 ? <button type="button" onClick={advance} className="eco-btn-primary justify-center sm:ml-auto">{t('requestProposal.next')} <ArrowRight size={15} /></button> : <button type="submit" disabled={sending} data-testid="request-proposal-submit" className="eco-btn-primary min-h-12 justify-center disabled:opacity-60">{sending ? <Loader2 size={16} className="animate-spin" /> : t('requestProposal.localNetwork')}</button>}</div>
            </form>
          </section>
        )}
      </div>
    </main>
  );
}
